import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { formatINR } from '@/lib/formatters';
import {
  Users, UserPlus, Flame, ThermometerSun, Snowflake, Clock,
  AlertTriangle, MapPin, FileText, IndianRupee, TrendingUp, Building2,
  CalendarCheck, Target
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend
} from 'recharts';

const KPI_CONFIGS = [
  { key: 'total_leads', label: 'Total Leads', icon: Users, color: 'text-blue-600', bg: 'bg-blue-50' },
  { key: 'new_today', label: 'New Today', icon: UserPlus, color: 'text-emerald-600', bg: 'bg-emerald-50' },
  { key: 'new_month', label: 'New This Month', icon: TrendingUp, color: 'text-indigo-600', bg: 'bg-indigo-50' },
  { key: 'hot_leads', label: 'Hot Leads', icon: Flame, color: 'text-red-600', bg: 'bg-red-50' },
  { key: 'warm_leads', label: 'Warm Leads', icon: ThermometerSun, color: 'text-amber-600', bg: 'bg-amber-50' },
  { key: 'cold_leads', label: 'Cold Leads', icon: Snowflake, color: 'text-blue-400', bg: 'bg-sky-50' },
  { key: 'unassigned', label: 'Unassigned', icon: AlertTriangle, color: 'text-orange-600', bg: 'bg-orange-50' },
  { key: 'followups_due', label: 'Follow-ups Due', icon: Clock, color: 'text-purple-600', bg: 'bg-purple-50' },
  { key: 'overdue_followups', label: 'Overdue', icon: AlertTriangle, color: 'text-red-500', bg: 'bg-red-50' },
  { key: 'visits_today', label: 'Visits Today', icon: MapPin, color: 'text-teal-600', bg: 'bg-teal-50' },
  { key: 'negotiations', label: 'Negotiations', icon: Target, color: 'text-pink-600', bg: 'bg-pink-50' },
  { key: 'total_bookings', label: 'Bookings', icon: FileText, color: 'text-emerald-700', bg: 'bg-emerald-50' },
  { key: 'total_booking_value', label: 'Booking Value', icon: IndianRupee, color: 'text-green-700', bg: 'bg-green-50', format: 'inr' },
  { key: 'payments_received', label: 'Collected', icon: IndianRupee, color: 'text-emerald-600', bg: 'bg-emerald-50', format: 'inr' },
  { key: 'outstanding', label: 'Outstanding', icon: IndianRupee, color: 'text-red-600', bg: 'bg-red-50', format: 'inr' },
  { key: 'visits_completed', label: 'Visits Done', icon: CalendarCheck, color: 'text-green-600', bg: 'bg-green-50' },
];

const STAGE_LABELS = {
  new: 'New', assigned: 'Assigned', contacted: 'Contacted', qualified: 'Qualified',
  visit_scheduled: 'Visit Sched.', visit_completed: 'Visit Done', revisit: 'Revisit',
  negotiation: 'Negotiation', token_received: 'Token', booked: 'Booked',
  lost: 'Lost', nurturing: 'Nurturing', first_call_pending: 'First Call',
};

const CHART_COLORS = ['#10B981', '#3B82F6', '#F59E0B', '#EC4899', '#8B5CF6', '#EF4444', '#06B6D4', '#F97316', '#84CC16', '#6366F1'];

export default function DashboardPage() {
  const { user } = useAuth();
  const [kpis, setKpis] = useState({});
  const [charts, setCharts] = useState({ pipeline: [], sources: [], temperature: [], projects: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [kpiRes, chartRes] = await Promise.all([
          api.get('/dashboard/kpis'),
          api.get('/dashboard/charts'),
        ]);
        setKpis(kpiRes.data);
        setCharts(chartRes.data);
      } catch (err) {
        console.error('Dashboard error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const roleKpis = () => {
    if (user?.role === 'finance') return KPI_CONFIGS.filter(k => ['total_bookings', 'total_booking_value', 'payments_received', 'outstanding', 'cancellations'].includes(k.key));
    if (user?.role === 'executive') return KPI_CONFIGS.filter(k => ['total_leads', 'hot_leads', 'followups_due', 'overdue_followups', 'visits_today', 'negotiations', 'total_bookings'].includes(k.key));
    return KPI_CONFIGS;
  };

  const pipelineData = charts.pipeline?.map(d => ({
    name: STAGE_LABELS[d.stage] || d.stage, count: d.count,
  })) || [];

  const sourceData = charts.sources?.map((d, i) => ({
    name: d.source?.replace(/_/g, ' ')?.replace(/\b\w/g, c => c.toUpperCase()) || 'Other',
    value: d.count, fill: CHART_COLORS[i % CHART_COLORS.length],
  })) || [];

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="dash-grid">
          {[1,2,3,4,5,6,7,8].map(i => (
            <div key={i} className="h-28 bg-white rounded-xl border border-slate-200 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">Dashboard</h1>
        <p className="text-slate-500 mt-1">Welcome back, {user?.name}. Here's your overview.</p>
      </div>

      {/* KPI Grid */}
      <div className="dash-grid" data-testid="kpi-grid">
        {roleKpis().map(kpi => {
          const Icon = kpi.icon;
          const value = kpis[kpi.key] ?? 0;
          return (
            <Card key={kpi.key} className="kpi-card border-slate-200 shadow-sm" data-testid={`kpi-${kpi.key}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-1">{kpi.label}</p>
                    <p className="text-2xl font-extrabold tracking-tight text-slate-900 font-mono">
                      {kpi.format === 'inr' ? formatINR(value) : value.toLocaleString('en-IN')}
                    </p>
                  </div>
                  <div className={`w-10 h-10 rounded-lg ${kpi.bg} flex items-center justify-center`}>
                    <Icon size={20} className={kpi.color} />
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Charts Row */}
      {user?.role !== 'finance' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Pipeline Chart */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold text-slate-800">Lead Pipeline</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={pipelineData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#64748B' }} angle={-35} textAnchor="end" height={70} />
                    <YAxis tick={{ fontSize: 11, fill: '#64748B' }} />
                    <Tooltip
                      contentStyle={{ borderRadius: '8px', border: '1px solid #E2E8F0', fontSize: '13px' }}
                    />
                    <Bar dataKey="count" fill="#10B981" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Source Distribution */}
          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold text-slate-800">Lead Sources</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={sourceData} cx="50%" cy="50%" outerRadius={90} innerRadius={45}
                      paddingAngle={2} dataKey="value"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      labelLine={false}
                    >
                      {sourceData.map((entry, i) => (
                        <Cell key={i} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Project-wise leads */}
      {charts.projects?.length > 0 && user?.role !== 'finance' && (
        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-slate-800">Project-wise Lead Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={charts.projects} layout="vertical" margin={{ top: 5, right: 30, left: 80, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis type="number" tick={{ fontSize: 12, fill: '#64748B' }} />
                  <YAxis dataKey="project" type="category" tick={{ fontSize: 12, fill: '#334155' }} width={120} />
                  <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #E2E8F0' }} />
                  <Bar dataKey="count" fill="#3B82F6" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
